import { useState } from 'react'
import { HiChevronDown } from 'react-icons/hi'
import useResponsive from 'utils/hooks/useResponsive'

// Card colapsable usada en el aside lateral del detalle de hallazgo.
// Abstrae el patron compartido entre TicketDetailsCard, TicketAttachmentsCard
// y TicketLinkedCard: wrapper rounded-xl + boton toggle con icono + titulo +
// chevron giratorio + body que se oculta cuando colapsado.
//
// Props:
// - icon: ReactNode renderizado a la izquierda del titulo
// - title: string del header
// - headerExtra: ReactNode opcional renderizado entre titulo y chevron (ej: count badge)
// - defaultExpanded: override del default (default = expandido en desktop, colapsado en mobile)
// - embedded: si true, no renderiza wrapper ni toggle; muestra children directo
// - children: contenido del body
const AsideCard = ({
    icon,
    title,
    headerExtra,
    defaultExpanded,
    embedded = false,
    children,
}) => {
    const { smaller } = useResponsive()
    const [isExpandedRaw, setIsExpandedRaw] = useState(
        () => defaultExpanded ?? !smaller['2xl']
    )
    const isExpanded = embedded || isExpandedRaw
    const isCollapsed = !embedded && !isExpanded

    const handleToggle = () => setIsExpandedRaw((v) => !v)

    if (embedded) {
        return <div>{children}</div>
    }

    return (
        <div
            className={[
                'rounded-xl border bg-white dark:bg-gray-800 mb-3 transition-all',
                isCollapsed
                    ? 'border-gray-200 dark:border-gray-700 px-3 py-2 shadow-none'
                    : 'border-gray-200 dark:border-gray-700 p-4 shadow-sm',
            ].join(' ')}
        >
            <button
                type="button"
                onClick={handleToggle}
                className={[
                    'flex items-center justify-between w-full gap-2 text-left rounded-lg cursor-pointer',
                    isCollapsed
                        ? 'py-1.5 -mx-1 px-1'
                        : 'pb-4 mb-4 border-b border-gray-100 dark:border-gray-700',
                ].join(' ')}
                aria-expanded={isExpanded}
            >
                <div className="flex items-center gap-1.5">
                    {icon}
                    <span className="font-semibold text-[13px] leading-none text-gray-900 dark:text-gray-100">
                        {title}
                    </span>
                    {headerExtra}
                </div>
                <span
                    className={[
                        'inline-flex items-center justify-center w-6 h-6 rounded-full text-gray-500 dark:text-gray-400 transition-colors',
                        isCollapsed
                            ? 'bg-gray-100 dark:bg-gray-700'
                            : 'bg-transparent',
                    ].join(' ')}
                >
                    <HiChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                        }`}
                    />
                </span>
            </button>

            <div className={isExpanded ? '' : 'hidden'}>{children}</div>
        </div>
    )
}

export default AsideCard
